import { forwardRef, Ref } from 'react';
import { FloatingArrow, FloatingNode, FloatingPortal, useFloating, FloatingFocusManager } from '@floating-ui/react';
import { cx } from '@linaria/atomic';
import { AnimatePresence, motion } from 'motion/react';
import { Text } from '../text';
import * as styles from './tooltip.styles';
import type { UseTooltipResponse } from './use-tooltip';

type TooltipProps = Omit<UseTooltipResponse['tooltipProps'], 'ref'> & {
  hasInteractiveContent?: boolean;
  showArrow?: boolean;
  className?: string;
};

type Props = Omit<React.ComponentPropsWithoutRef<'div'>, keyof TooltipProps> & TooltipProps;

// eslint-disable-next-line react/display-name
const TooltipContainer = forwardRef(
  (
    {
      isOpen,
      children,
      arrowRef,
      context,
      theme,
      showArrow = true,
      className,
      ...tooltipProps
    }: Omit<Props, 'floatingRef' | 'hasInteractiveContent' | 'nodeId' | 'initial' | 'animate' | 'exit' | 'transition'>,
    ref: Ref<HTMLDivElement>
  ) => {
    const isChildrenString = typeof children === 'string';

    const { onAnimationStart, onDragStart, onDragEnd, onDrag, ...motionCompatibleProps } = tooltipProps;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ ease: 'easeInOut', duration: 0.25 }}
        ref={ref}
        className={cx(styles.tooltipBaseStyle, styles.tooltipThemeStyles[theme], className)}
        {...motionCompatibleProps}
      >
        {isChildrenString ? <Text className={styles.tooltipTextStyle}>{children}</Text> : <>{children}</>}
        {showArrow && (
          <FloatingArrow
            className='tooltip-floating-arrow'
            ref={arrowRef}
            context={context}
            height={8}
            width={16}
            tipRadius={2}
          />
        )}
      </motion.div>
    );
  }
);

// eslint-disable-next-line react/display-name
export const Tooltip = forwardRef(
  ({ isOpen, nodeId, hasInteractiveContent, floatingRef, ...props }: Props, ref: Ref<HTMLDivElement>) => {
    useFloating({ nodeId: nodeId });

    return (
      <FloatingNode id={nodeId}>
        <FloatingPortal>
          <AnimatePresence>
            {isOpen && (
              <ConditionalFloatingFocusManager
                hasInteractiveContent={hasInteractiveContent}
                floatingRef={floatingRef}
                context={props.context}
              >
                <TooltipContainer {...props} isOpen={isOpen} ref={ref} />
              </ConditionalFloatingFocusManager>
            )}
          </AnimatePresence>
        </FloatingPortal>
      </FloatingNode>
    );
  }
);

type ConditionalFloatingFocusManagerProps = {
  children: React.ReactElement;
  context: TooltipProps['context'];
  hasInteractiveContent?: boolean;
  floatingRef?: TooltipProps['floatingRef'];
};
const ConditionalFloatingFocusManager = ({
  hasInteractiveContent,
  context,
  children,
  floatingRef,
}: ConditionalFloatingFocusManagerProps) => {
  return !hasInteractiveContent ? (
    <>{children}</>
  ) : (
    <FloatingFocusManager context={context} initialFocus={floatingRef}>
      {children}
    </FloatingFocusManager>
  );
};
